import React,{useEffect,useRef,useState} from 'react';
import {NavLink,Outlet,useLocation,useNavigate} from 'react-router-dom';
import {legalVaultService} from '../services/legalVaultService';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiGrid,FiFileText,FiLayers,FiLink,FiDownload,FiSettings,FiX,FiSearch,FiBell,FiUser,FiLogOut,
  FiArrowRight,FiHelpCircle,FiExternalLink,FiPlus,FiMoreHorizontal
}=FiIcons;

export default function Layout() {
  const [searchQuery,setSearchQuery]=useState('');
  const [searchDocuments,setSearchDocuments]=useState([]);
  const [searchOpen,setSearchOpen]=useState(false);
  const [searchLoading,setSearchLoading]=useState(false);
  const [moreOpen,setMoreOpen]=useState(false);
  const desktopSearchInputRef=useRef(null);
  const mobileSearchInputRef=useRef(null);
  const contentScrollRef=useRef(null);
  const navigate=useNavigate();
  const location=useLocation();
  const user=legalVaultService.getCurrentUser();

  const canEditDocuments=legalVaultService.hasAdminAccess();
  const canManageSettings=legalVaultService.hasSettingsAccess();
  const externalHubUrl=`${window.location.origin}/#/legal`;

  const navItems=[
    {path:'/',label:'Dashboard',icon:FiGrid},
    {path:'/documents',label:'Documents',icon:FiFileText},
    ...(canEditDocuments ? [{path:'/templates',label:'Templates',icon:FiLayers}] : []),
    ...(canManageSettings ? [{path:'/public-links',label:'Public Links',icon:FiLink}] : []),
    ...(canManageSettings ? [{path:'/exports',label:'Exports',icon:FiDownload}] : []),
    ...(canManageSettings ? [{path:'/settings',label:'Settings',icon:FiSettings}] : []),
    {path:'/help',label:'Help & Support',icon:FiHelpCircle},
  ];

  useEffect(()=> {
    const media=window.matchMedia('(max-width: 1180px)');
    const applyMobileLock=()=> {
      const method=media.matches ? 'add' : 'remove';
      document.documentElement.classList[method]('legal-vault-mobile-lock');
      document.body.classList[method]('legal-vault-mobile-lock');
    };
    applyMobileLock();
    media.addEventListener('change',applyMobileLock);
    return ()=> {
      media.removeEventListener('change',applyMobileLock);
      document.documentElement.classList.remove('legal-vault-mobile-lock');
      document.body.classList.remove('legal-vault-mobile-lock');
    };
  },[]);

  useEffect(()=> {
    setMoreOpen(false);
    setSearchOpen(false);
    setSearchQuery('');
    window.requestAnimationFrame(()=> {
      contentScrollRef.current?.scrollTo({top:0,left:0,behavior:'auto'});
    });
  },[location.pathname,location.search]);

  useEffect(()=> {
    const handleShortcut=(event)=> {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase()==='k') {
        event.preventDefault();
        if (window.innerWidth<=1180) {
          setSearchOpen(true);
          window.requestAnimationFrame(()=> mobileSearchInputRef.current?.focus());
        } else {
          desktopSearchInputRef.current?.focus();
        }
      }
      if (event.key==='Escape') {
        setSearchOpen(false);
        setMoreOpen(false);
        desktopSearchInputRef.current?.blur();
        mobileSearchInputRef.current?.blur();
      }
    };
    window.addEventListener('keydown',handleShortcut);
    return ()=> window.removeEventListener('keydown',handleShortcut);
  },[]);

  useEffect(()=> {
    if (!moreOpen && !searchOpen) return undefined;
    const previous=document.body.style.overflow;
    if (window.innerWidth<=1180) document.body.style.overflow='hidden';
    return ()=> {document.body.style.overflow=previous;};
  },[moreOpen,searchOpen]);

  const loadSearchDocuments=async()=> {
    if (searchDocuments.length>0 || searchLoading) return;
    setSearchLoading(true);
    try {
      const docs=await legalVaultService.getDocuments();
      setSearchDocuments(Array.isArray(docs) ? docs : []);
    } catch {
      setSearchDocuments([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const openSearch=()=> {
    setSearchOpen(true);
    loadSearchDocuments();
    window.requestAnimationFrame(()=> mobileSearchInputRef.current?.focus());
  };

  const handleSearchFocus=()=> {
    setSearchOpen(true);
    loadSearchDocuments();
  };

  const normalizedQuery=searchQuery.trim().toLowerCase();
  const searchResults=normalizedQuery
    ? searchDocuments.filter((doc)=> {
        const searchable=[doc.title,doc.type,doc.currentVersion,doc.tags].filter(Boolean).join(' ').toLowerCase();
        return searchable.includes(normalizedQuery);
      }).slice(0,8)
    : [];

  const openSearchResult=(doc)=> {
    setSearchQuery('');
    setSearchOpen(false);
    navigate(`/documents/${doc.id}`);
  };

  const handleLogout=()=> {
    legalVaultService.logout();
    navigate('/login',{replace:true});
  };

  const openExternalHub=()=> window.open(externalHubUrl,'_blank','noopener,noreferrer');
  const isActive=(path)=> path==='/' ? location.pathname==='/' : location.pathname.startsWith(path);

  const mobileMoreItems=[
    ...(canEditDocuments ? [{label:'Templates',icon:FiLayers,onClick:()=> navigate('/templates')}] : []),
    ...(canManageSettings ? [{label:'Public Links',icon:FiLink,onClick:()=> navigate('/public-links')}] : []),
    ...(canManageSettings ? [{label:'Exports',icon:FiDownload,onClick:()=> navigate('/exports')}] : []),
    ...(canManageSettings ? [{label:'Settings',icon:FiSettings,onClick:()=> navigate('/settings')}] : []),
    {label:'Help & Support',icon:FiHelpCircle,onClick:()=> navigate('/help')},
    {label:'External Hub',icon:FiExternalLink,onClick:openExternalHub},
  ];

  return (
    <div className="legal-vault-shell min-h-[100dvh] w-full max-w-full flex bg-slate-50 font-sans text-slate-800 overflow-hidden">
      <aside className="desktop-shell-only w-64 min-h-[100dvh] bg-slate-900 text-slate-300 flex-col overflow-hidden sticky top-0">
        <div className="h-16 flex items-center px-6 bg-slate-950 border-b border-slate-800">
          <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center text-white font-bold text-sm mr-3">LV</div>
          <span className="text-white font-semibold text-lg tracking-tight">Legal Vault</span>
        </div>
        <div className="px-4 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Menu</div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item)=>(
            <NavLink key={item.path} to={item.path} className={({isActive:active})=> `flex items-center px-3 py-2.5 rounded-lg transition-colors ${active ? 'bg-blue-600 text-white font-medium' : 'hover:bg-slate-800 hover:text-white'}`}>
              <SafeIcon icon={item.icon} className="text-lg mr-3" />{item.label}
            </NavLink>
          ))}
          <div className="pt-3 mt-3 border-t border-slate-800">
            <a href={externalHubUrl} target="_blank" rel="noopener noreferrer" className="flex items-center px-3 py-2.5 rounded-lg text-[#e7d39e] hover:bg-slate-800 hover:text-white transition-colors">
              <SafeIcon icon={FiExternalLink} className="text-lg mr-3" /><span className="flex-1">External Hub</span><span className="text-[10px] text-slate-500">NEW TAB</span>
            </a>
          </div>
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center flex-1 min-w-0">
              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-white mr-3"><SafeIcon icon={FiUser} /></div>
              <div className="flex-1 min-w-0"><p className="text-white font-medium truncate" title={user?.email || 'User'}>{user?.email || 'User'}</p><p className="text-slate-500 text-xs">{user?.role || 'viewer'}</p></div>
            </div>
            <button onClick={handleLogout} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors" title="Logout"><SafeIcon icon={FiLogOut} /></button>
          </div>
        </div>
      </aside>

      <main className="legal-vault-main flex-1 flex flex-col min-w-0 min-h-[100dvh] overflow-hidden">
        <header className="desktop-shell-only h-16 bg-white border-b border-slate-200 items-center justify-between px-6 z-30 shadow-sm">
          <h1 className="text-xl font-semibold text-slate-800">Admin Portal</h1>
          <div className="flex items-center space-x-3">
            <a href={externalHubUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 hover:text-blue-600"><SafeIcon icon={FiExternalLink} /> External Hub</a>
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
              <input ref={desktopSearchInputRef} type="search" value={searchQuery} onFocus={handleSearchFocus} onChange={(event)=> {setSearchQuery(event.target.value);setSearchOpen(true);}} placeholder="Search documents..." aria-label="Quick search documents" className="pl-10 pr-14 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-72" />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-medium text-slate-400 border border-slate-200 bg-white rounded px-1.5 py-0.5">Ctrl K</span>
              {searchOpen && <div className="absolute right-0 mt-2 w-[380px] bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden z-50"><SearchResults loading={searchLoading} query={searchQuery} normalizedQuery={normalizedQuery} results={searchResults} onOpen={openSearchResult} onBrowse={()=> {setSearchOpen(false);navigate('/documents');}} canCreate={canEditDocuments} onCreate={()=> {setSearchOpen(false);navigate('/documents/new/edit');}} /></div>}
            </div>
            <button onClick={()=> navigate('/documents?filter=review')} className="text-slate-400 hover:text-blue-600 p-2 rounded-lg hover:bg-slate-50" title="Documents due for review"><SafeIcon icon={FiBell} className="text-xl" /></button>
          </div>
        </header>

        <header className="mobile-shell-only mobile-top-bar h-16 bg-white border-b border-slate-200 items-center justify-between z-30 shadow-sm">
          <div className="flex items-center min-w-0">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-sm mr-3 flex-shrink-0">LV</div>
            <div className="min-w-0"><p className="text-sm font-bold text-slate-900 truncate">Legal Vault</p><p className="text-[11px] text-slate-500 truncate">BPA Solutions</p></div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button onClick={openSearch} className="w-11 h-11 rounded-xl flex items-center justify-center text-slate-600 hover:bg-slate-100" aria-label="Search documents"><SafeIcon icon={FiSearch} className="text-[22px]" /></button>
            <button onClick={()=> navigate('/documents?filter=review')} className="w-11 h-11 rounded-xl flex items-center justify-center text-slate-600 hover:bg-slate-100" aria-label="Review centre"><SafeIcon icon={FiBell} className="text-[22px]" /></button>
          </div>
        </header>

        <div ref={contentScrollRef} className="legal-vault-scroll flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden p-4 sm:p-6 desktop-content-padding mobile-content-safe">
          <Outlet />
        </div>
      </main>

      <nav className="mobile-shell-only mobile-bottom-nav fixed left-0 right-0 bottom-0 z-[70] bg-white border-t border-slate-200 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] items-start justify-around px-2 pt-2">
        <MobileNavButton label="Dashboard" icon={FiGrid} active={isActive('/')} onClick={()=> navigate('/')} />
        <MobileNavButton label="Documents" icon={FiFileText} active={isActive('/documents')} onClick={()=> navigate('/documents')} />
        <button type="button" onClick={canEditDocuments ? ()=> navigate('/documents/new/edit') : openExternalHub} className="relative -mt-7 w-[72px] min-w-[72px] min-h-[74px] flex flex-col items-center justify-start text-blue-700" aria-label={canEditDocuments ? 'Create new document' : 'Open external hub'}>
          <span className="w-14 h-14 rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-600/30 border-4 border-white flex items-center justify-center"><SafeIcon icon={canEditDocuments ? FiPlus : FiExternalLink} className="text-[28px]" /></span>
          <span className="text-[10px] font-bold mt-1">{canEditDocuments ? 'New' : 'Hub'}</span>
        </button>
        <MobileNavButton label="Review" icon={FiBell} active={location.search.includes('filter=review')} onClick={()=> navigate('/documents?filter=review')} />
        <MobileNavButton label="More" icon={FiMoreHorizontal} active={moreOpen} onClick={()=> setMoreOpen(true)} />
      </nav>

      {moreOpen && (
        <div className="mobile-shell-only fixed inset-0 z-[90] bg-slate-950/45 items-end" onMouseDown={()=> setMoreOpen(false)}>
          <section className="w-full max-w-full bg-white rounded-t-3xl shadow-2xl p-4 mobile-sheet-safe" onMouseDown={(event)=> event.stopPropagation()} aria-label="More navigation">
            <div className="w-10 h-1 rounded-full bg-slate-300 mx-auto mb-4" />
            <div className="flex items-center justify-between mb-4"><div className="min-w-0"><h2 className="text-lg font-bold text-slate-900">More</h2><p className="text-xs text-slate-500">Administration and support</p></div><button onClick={()=> setMoreOpen(false)} className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 flex-shrink-0" aria-label="Close more menu"><SafeIcon icon={FiX} className="text-xl" /></button></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{mobileMoreItems.map((item)=><button key={item.label} onClick={()=> {setMoreOpen(false);item.onClick();}} className="min-h-[82px] min-w-0 rounded-2xl border border-slate-200 bg-slate-50 hover:bg-slate-100 p-3 text-left"><SafeIcon icon={item.icon} className="text-2xl text-blue-600 mb-2" /><span className="block text-sm font-semibold text-slate-800 break-words">{item.label}</span></button>)}</div>
            <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between gap-3"><div className="min-w-0"><p className="text-sm font-semibold text-slate-900 truncate">{user?.email || 'User'}</p><p className="text-xs text-slate-500 capitalize">{user?.role || 'viewer'}</p></div><button onClick={handleLogout} className="min-h-11 px-4 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm font-semibold inline-flex items-center flex-shrink-0"><SafeIcon icon={FiLogOut} className="mr-2" /> Log out</button></div>
          </section>
        </div>
      )}

      {searchOpen && (
        <div className="mobile-shell-only fixed inset-0 z-[95] bg-slate-50 flex-col overflow-hidden">
          <div className="bg-white border-b border-slate-200 px-4 pt-[max(12px,env(safe-area-inset-top))] pb-3 flex items-center gap-2 flex-shrink-0">
            <div className="relative flex-1 min-w-0"><SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input ref={mobileSearchInputRef} type="search" value={searchQuery} onChange={(event)=> setSearchQuery(event.target.value)} placeholder="Search documents..." className="w-full min-h-12 pl-10 pr-4 rounded-xl border border-slate-300 bg-slate-50 text-base focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
            <button onClick={()=> setSearchOpen(false)} className="w-12 h-12 rounded-xl flex items-center justify-center text-slate-600 bg-slate-100 flex-shrink-0" aria-label="Close search"><SafeIcon icon={FiX} className="text-xl" /></button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 mobile-sheet-safe"><div className="w-full max-w-full bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"><SearchResults loading={searchLoading} query={searchQuery} normalizedQuery={normalizedQuery} results={searchResults} onOpen={openSearchResult} onBrowse={()=> {setSearchOpen(false);navigate('/documents');}} canCreate={canEditDocuments} onCreate={()=> {setSearchOpen(false);navigate('/documents/new/edit');}} /></div></div>
        </div>
      )}
    </div>
  );
}

function MobileNavButton({label,icon,active,onClick}) {
  return <button type="button" onClick={onClick} className={`min-w-[58px] min-h-[58px] px-2 rounded-xl flex flex-col items-center justify-center gap-1 transition-colors ${active ? 'text-blue-700 bg-blue-50' : 'text-slate-500'}`}><SafeIcon icon={icon} className="text-[23px]" /><span className="text-[10px] font-semibold">{label}</span></button>;
}

function SearchResults({loading,query,normalizedQuery,results,onOpen,onBrowse,canCreate,onCreate}) {
  if (loading) return <div className="p-4 text-sm text-slate-500">Loading documents...</div>;
  return (
    <>
      {normalizedQuery ? (
        results.length>0 ? <div className="py-2">{results.map((doc)=><button key={doc.id} type="button" onMouseDown={(event)=> event.preventDefault()} onClick={()=> onOpen(doc)} className="w-full min-w-0 px-4 py-3.5 text-left hover:bg-slate-50 flex items-center justify-between gap-3"><div className="min-w-0"><p className="text-sm font-semibold text-slate-900 truncate">{doc.title}</p><p className="text-xs text-slate-500 truncate">{doc.type} · {doc.status}{doc.currentVersion ? ` · ${doc.currentVersion}` : ''}</p></div><SafeIcon icon={FiArrowRight} className="text-slate-400 flex-shrink-0" /></button>)}</div>
        : <div className="p-4 text-sm text-slate-500">No documents match “{query}”.</div>
      ) : <div className="p-4 text-sm text-slate-500">Type a document title, type, tag or version number.</div>}
      <div className="border-t border-slate-100 p-2 flex gap-2"><button type="button" onMouseDown={(event)=> event.preventDefault()} onClick={onBrowse} className="flex-1 min-w-0 min-h-11 px-3 py-2 text-xs font-semibold text-slate-700 bg-slate-50 hover:bg-slate-100 rounded-lg">Browse Documents</button>{canCreate && <button type="button" onMouseDown={(event)=> event.preventDefault()} onClick={onCreate} className="flex-1 min-w-0 min-h-11 px-3 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg">New Document</button>}</div>
    </>
  );
}